import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Package, Plus, Trash2, Pencil, X, Sun, Moon, Check } from "lucide-react";

// Croquis des types de protections — servis par le backend (noms ASCII propres, compatibles Windows)
const ICON_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/inc/icon`;
const ic = (key, group, label) => ({ key, group, label, url: `${ICON_BASE}/${key}` });
export const PROTECTION_ICONS = [
  ic("anatomique", "protection", "Serviette anatomique"),
  ic("change_complet", "protection", "Change complet (pattes)"),
  ic("slip_1", "protection", "Slip absorbant"),
  ic("slip_2", "protection", "Slip absorbant (nuit)"),
  ic("protection_droite", "protection", "Protection droite"),
  ic("sachet", "protection", "Sachet de protection"),
  ic("protection_ceinture", "protection", "Protection ceinture"),
  ic("anatomique_longue", "protection", "Protection anatomique longue"),
  ic("change_anatomique", "protection", "Change anatomique"),
  ic("alese", "autre", "Alèse"),
  ic("slip_maintien", "autre", "Slip de maintien"),
  ic("gants", "autre", "Gants jetables"),
];
export const iconUrlFromKey = (key) => PROTECTION_ICONS.find((i) => i.key === key)?.url || "";
const iconUrl = (key) => PROTECTION_ICONS.find((i) => i.key === key)?.url;

const empty = { name: "", category: "JOUR", package_size: 1, carton_size: 0, price_per_carton: 0, reference: "", icon: "" };

export function ProductsDialog({ open, onOpenChange, products, onReload }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const active = products.filter((p) => p.active !== false).sort((a, b) => a.display_order - b.display_order);

  const reset = () => { setForm(empty); setEditId(null); };

  const save = async () => {
    if (!form.name.trim()) return toast.error(t("inc.pd.nameRequired"));
    const payload = {
      name: form.name.trim(),
      category: form.category,
      package_size: Number(form.package_size) || 1,
      carton_size: Number(form.carton_size) || 0,
      price_per_carton: Number(form.price_per_carton) || 0,
      reference: (form.reference || "").trim(),
      icon: form.icon || "",
      display_order: editId ? (products.find((p) => p.id === editId)?.display_order || 0) : active.length + 1,
      active: true,
    };
    try {
      if (editId) await api.put(`/inc/products/${editId}`, payload);
      else await api.post("/inc/products", payload);
      toast.success(editId ? t("inc.pd.productEdited") : t("inc.pd.productAdded"));
      reset();
      await onReload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const edit = (p) => { setEditId(p.id); setForm({ name: p.name, category: p.category, package_size: p.package_size, carton_size: p.carton_size || 0, price_per_carton: p.price_per_carton || 0, reference: p.reference || "", icon: p.icon || "" }); };

  const del = async (p) => {
    if (!window.confirm(t("inc.pd.confirmDisable", { name: p.name }))) return;
    try { await api.delete(`/inc/products/${p.id}`); toast.success(t("inc.pd.productDisabled")); if (editId === p.id) reset(); await onReload(); }
    catch (e) { toast.error(t("inc.pd.deleteFailed")); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Package className="h-5 w-5 text-sky-400" /> {t("inc.pd.title")}</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">{t("inc.pd.desc")}</DialogDescription>

        {/* Liste des produits */}
        <div className="max-h-[42vh] overflow-y-auto rounded-xl border border-white/10" data-testid="inc-products-list">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#161427] text-[11px] uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">{t("inc.pd.colProduct")}</th>
                <th className="px-2 py-2 text-center">{t("inc.pd.colRef")}</th>
                <th className="px-2 py-2 text-center">{t("inc.pd.colCat")}</th>
                <th className="px-2 py-2 text-center">{t("inc.pd.colPackage")}</th>
                <th className="px-2 py-2 text-center">{t("inc.pd.colCarton")}</th>
                <th className="px-2 py-2 text-right">{t("inc.pd.colPrice")}</th>
                <th className="px-2 py-2 text-right">{t("inc.pd.colActions")}</th>
              </tr>
            </thead>
            <tbody>
              {active.map((p) => (
                <tr key={p.id} className={`border-t border-white/5 ${editId === p.id ? "bg-sky-500/10" : ""}`} data-testid={`inc-product-row-${p.id}`}>
                  <td className="px-3 py-2 font-medium">
                    <span className="inline-flex items-center gap-2">
                      {p.icon && iconUrl(p.icon)
                        ? <img src={iconUrl(p.icon)} alt="" className="h-7 w-7 object-contain rounded-md bg-white/5 p-0.5" />
                        : <span className={`inline-block h-2 w-2 rounded-full ${p.category === "JOUR" ? "bg-amber-400" : p.category === "NUIT" ? "bg-indigo-400" : "bg-teal-400"}`} />}
                      {p.name}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-center text-xs font-mono text-muted-foreground">{p.reference || "—"}</td>
                  <td className="px-2 py-2 text-center text-xs">
                    {p.category === "JOUR" ? <span className="text-amber-300"><Sun className="h-3.5 w-3.5 inline" /> {t("inc.pd.day")}</span>
                      : p.category === "NUIT" ? <span className="text-indigo-300"><Moon className="h-3.5 w-3.5 inline" /> {t("inc.pd.night")}</span>
                      : <span className="text-teal-300">{t("inc.pd.other")}</span>}
                  </td>
                  <td className="px-2 py-2 text-center">{p.package_size}</td>
                  <td className="px-2 py-2 text-center">{p.carton_size || "—"}</td>
                  <td className="px-2 py-2 text-right">{p.price_per_carton ? `${Number(p.price_per_carton).toLocaleString("fr-BE", { minimumFractionDigits: 2 })} €` : "—"}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    <button onClick={() => edit(p)} data-testid={`inc-product-edit-${p.id}`} className="text-sky-300 hover:text-sky-200 mr-2" title={t("inc.pd.edit")}><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => del(p)} data-testid={`inc-product-del-${p.id}`} className="text-red-400 hover:text-red-300" title={t("inc.pd.disable")}><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
              {!active.length && <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground text-sm">{t("inc.pd.empty")}</td></tr>}
            </tbody>
          </table>
        </div>

        {/* Formulaire */}
        <div className="rounded-xl border border-white/10 p-4 mt-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">{editId ? <><Pencil className="h-4 w-4 text-sky-300" /> {t("inc.pd.editTitle")}</> : <><Plus className="h-4 w-4 text-emerald-300" /> {t("inc.pd.newTitle")}</>}</h3>
            {editId && <button onClick={reset} className="text-xs text-muted-foreground hover:text-white flex items-center gap-1" data-testid="inc-product-cancel-edit"><X className="h-3.5 w-3.5" /> {t("inc.pd.cancel")}</button>}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3">
            <div className="col-span-2 space-y-1.5"><Label className="text-xs">{t("inc.pd.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("inc.pd.namePlaceholder")} data-testid="inc-product-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("inc.pd.reference")}</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder={t("inc.pd.refPlaceholder")} data-testid="inc-product-reference" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("inc.pd.category")}</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => {
                const grp = v === "AUTRE" ? "autre" : "protection";
                const ok = PROTECTION_ICONS.find((i) => i.key === f.icon)?.group === grp;
                return { ...f, category: v, icon: ok ? f.icon : "" };
              })}>
                <SelectTrigger data-testid="inc-product-category"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="JOUR">{t("inc.pd.day")}</SelectItem><SelectItem value="NUIT">{t("inc.pd.night")}</SelectItem><SelectItem value="AUTRE">{t("inc.pd.other")}</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">{t("inc.pd.packageU")}</Label><Input type="number" min="1" value={form.package_size} onChange={(e) => setForm({ ...form, package_size: e.target.value })} data-testid="inc-product-package" /></div>
            <div className="space-y-1.5"><Label className="text-xs">{t("inc.pd.cartonP")}</Label><Input type="number" min="0" value={form.carton_size} onChange={(e) => setForm({ ...form, carton_size: e.target.value })} data-testid="inc-product-carton" /></div>
            <div className="col-span-2 sm:col-span-1 space-y-1.5"><Label className="text-xs">{t("inc.pd.priceEur")}</Label><Input type="number" min="0" step="0.01" value={form.price_per_carton} onChange={(e) => setForm({ ...form, price_per_carton: e.target.value })} data-testid="inc-product-price" /></div>
          </div>

          {/* Sélecteur d'image de la protection */}
          <div className="mt-4">
            <Label className="text-xs">{t("inc.pd.iconLabel")}</Label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2.5 mt-2">
              {PROTECTION_ICONS.filter((ic) => ic.group === (form.category === "AUTRE" ? "autre" : "protection")).map((ic) => {
                const selected = form.icon === ic.key;
                return (
                  <button type="button" key={ic.key} data-testid={`inc-product-icon-${ic.key}`}
                    onClick={() => setForm({ ...form, icon: selected ? "" : ic.key })}
                    className={`relative group rounded-xl border p-2 flex items-center justify-center transition ${selected ? "border-sky-400 bg-sky-500/10 ring-2 ring-sky-400/40" : "border-white/10 hover:border-sky-400/40 hover:bg-white/5"}`}>
                    {selected && <span className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-sky-500 text-white flex items-center justify-center shadow"><Check className="h-3 w-3" /></span>}
                    <img src={ic.url} alt="" className="h-16 w-16 object-contain" />
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <Button className="bg-sky-600 hover:bg-sky-500" onClick={save} data-testid="inc-product-save">{editId ? t("inc.pd.btnSave") : t("inc.pd.btnAdd")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
